import { Controller } from 'stimulus'
import {
  defaultPropertiesAction,
  ACTION_CREATE_PAGE_EVENT
} from '../constants/analytics'

export default class extends Controller {
  static targets = [
    'shareExportBtn',
    'downloadLink',
    'exportNameLabel',
    'deleteExportBtn',
    'exportSearchInput'
  ];

  connect() {
    this._exportFiltersTriggeredCounter = 0
    this._exportsIndexFilters = ['date', 'source', 'type']
    this._exportCreateFilters = [
      'labels',
      'issues',
      'document_type',
      'date',
      'location',
      'priority',
      'position'
    ]

    if (!document.documentElement.hasAttribute('data-turbolinks-preview')) {
      this.sendPageLoad()
    }
  }

  sendPageLoad() {
    const name = this.pathnameToEventName[window.location.pathname] || null
    const properties =
      this.pathnameToEventProperties[window.location.pathname] || null

    if (name == ACTION_CREATE_PAGE_EVENT) {
      analytics.track(name, properties)
    } else {
      analytics.page(name, properties)
    }
  }

  sendExportCreatedOrUpdated(event) {
    const exportId = event.detail.exportId
    const exportData = event.detail.exportData
    const searchAttributes = exportData.search_attributes
    const filtersApplied = this.getFiltersApplied(
      this._exportCreateFilters,
      searchAttributes
    )

    const eventName = exportId ? 'Export Updated' : 'Export Created'
    const properties = {
      export_filters_applied: filtersApplied,
      export_query_string: '',
      export_id: exportId,
      export_name: exportData.title,
      export_locations_applied: this.isPresent(searchAttributes.locations),
      export_issues_applied: this.isPresent(searchAttributes.issues_ids),
      export_labels_applied: this.isPresent(searchAttributes.labels_ids),
      export_positions_applied: this.isPresent(searchAttributes.positions),
      export_priorities_applied: this.isPresent(searchAttributes.priorities),
      export_type_applied: this.isPresent(searchAttributes.types)
    }

    analytics.track(eventName, properties)
  }

  sendExportShared() {
    const exportId = this.shareExportBtnTarget.dataset.analyticsExportId
    const exportName = this.shareExportBtnTarget.dataset.analyticsExportName

    const eventName = 'Export Shared'
    const properties = {
      export_id: exportId,
      bulk: 'false',
      export_name: exportName
    }

    analytics.track(eventName, properties)
  }

  sendExportDownload(event) {
    const exportId = event.currentTarget.dataset.analyticsExportId
    const exportName = event.currentTarget.dataset.analyticsExportName

    const eventName = 'Export Downloaded'
    const properties = {
      export_id: exportId,
      bulk: 'false',
      export_name: exportName
    }

    analytics.track(eventName, properties)
  }

  sendExportBulkDownload(event) {
    const exportIds = event.detail.downloadedExportsIds
    const exportNames = this.getExportNames(exportIds)

    const eventName = 'Export Downloaded'
    const properties = {
      export_id: exportIds,
      bulk: 'true',
      export_name: exportNames
    }

    analytics.track(eventName, properties)
  }

  sendExportDelete() {
    const exportId = this.deleteExportBtnTarget.dataset.analyticsExportId
    const exportName = this.deleteExportBtnTarget.dataset.analyticsExportName

    const eventName = 'Export Deleted'
    const properties = {
      export_id: exportId,
      bulk: 'false',
      export_name: exportName
    }

    analytics.track(eventName, properties)
  }

  sendExportBulkDelete(event) {
    const exportIds = event.detail.exportsIdsToDelete
    const exportNames = this.getExportNames(exportIds)

    const eventName = 'Export Deleted'
    const properties = {
      export_id: exportIds,
      bulk: 'true',
      export_name: exportNames
    }

    analytics.track(eventName, properties)
  }

  sendDataSearched(event) {
    const searchText = this.exportSearchInputTarget.value
    const filtersObject = event.detail.filters_applied
    const filtersApplied = this.getFiltersApplied(
      this._exportsIndexFilters,
      filtersObject
    )

    if (searchText == '' && filtersApplied.length == 0) {
      return
    }

    const eventName = 'Data Searched'
    const properties = {
      dataset_type: 'Workflow Data',
      content_type: 'Reporting',
      entity_type: 'Exports',
      jurisdiction_country: null,
      jurisdiction_state: null,
      keywords: searchText,
      filters_applied: filtersApplied,
      search_location: 'Exports Index Page'
    }

    analytics.track(eventName, properties)
  }

  // sendActionsFilterForm sends both 'Filter Applied' and 'Data Searched'
  sendActionsFilterForm(event) {
    const filtersObject = event.detail.filters_applied
    const searchAttributes = filtersObject.search_attributes
    const filtersApplied = {}

    // 'Filter Applied' specific
    let start_date = null
    let end_date = null

    // 'Data Searched' specific
    let search_text = null

    if (searchAttributes) {
      if (this.isPresent(searchAttributes.labels_ids)) {
        filtersApplied.labels_ids = searchAttributes.labels_ids
      }
      if (this.isPresent(searchAttributes.action_types_ids)) {
        filtersApplied.action_types_ids = searchAttributes.action_types_ids
      }
      if (this.isPresent(searchAttributes.issues_ids)) {
        filtersApplied.issues_ids = searchAttributes.issues_ids
      }
      if (this.isPresent(searchAttributes.attendees_ids)) {
        filtersApplied.attendees_ids = searchAttributes.attendees_ids
      }
      if (this.isPresent(searchAttributes.linked_item_ids)) {
        filtersApplied.linked_item_ids = searchAttributes.linked_item_ids
      }
      if (this.isPresent(searchAttributes.creators_ids)) {
        filtersApplied.creators_ids = searchAttributes.creators_ids
      }
      if (this.isPresent(searchAttributes.start_date)) {
        start_date = searchAttributes.start_date
      }
      if (this.isPresent(searchAttributes.end_date)) {
        end_date = searchAttributes.end_date
      }
      if (this.isPresent(searchAttributes.search_text)) {
        search_text = searchAttributes.search_text
      }
    }

    // isFilterApplied relies on search_text being set above
    function isFilterApplied() {
      return search_text === null
    }

    function isDataSearched() {
      return !isFilterApplied()
    }

    function eventName() {
      if (isFilterApplied()) {
        return 'Filter Applied'
      } else {
        return 'Data Searched'
      }
    }

    const properties = {
      ...defaultPropertiesAction,
      filters_applied: filtersApplied
    }

    if (isFilterApplied()) {
      properties.filter_date_start = start_date
      properties.filter_end_date = end_date
    }

    if (isDataSearched()) {
      properties.search_page = 'Actions Page.'
      properties.search_screen = 'Actions'
      properties.search_text = search_text
      properties.search_query = $.param(searchAttributes)
    }

    analytics.track(eventName(), properties)
  }

  sendExportBuilderLaunched() {
    if (this._exportFiltersTriggeredCounter === 1) {
      analytics.track('Export Builder Launched')
    }

    this._exportFiltersTriggeredCounter += 1
  }

  sendActionsExportCreate(event) {
    const exportId = event.detail.exportId || null
    const exportData = event.detail.exportData
    const searchAttributes = exportData.search_attributes

    const eventName = 'Export Created'
    const properties = {
      ...defaultPropertiesAction,
      action_export_results: null,
      export_all: !!event.detail.exportAll,
      export_id: exportId,
      export_name: exportData.title || null,
      export_button_source: null,
      export_locations_applied: null,
      export_issues_applied: null,
      export_labels_applied: null,
      export_positions_applied: null,
      export_priorities_applied: null,
      export_type_applied: null,
      export_origin: window.location.origin + window.location.pathname,
      export_entity_types: ['Actions'],
      item_count: event.detail.itemCount
    }

    if (searchAttributes) {
      const filtersApplied = this.getFiltersApplied(
        this._exportCreateFilters,
        searchAttributes
      )

      properties.export_locations_applied = this.isPresent(
        searchAttributes.locations
      )
      properties.export_issues_applied = this.isPresent(
        searchAttributes.issues_ids
      )
      properties.export_labels_applied = this.isPresent(
        searchAttributes.labels_ids
      )
      properties.export_positions_applied = this.isPresent(
        searchAttributes.positions
      )
      properties.export_priorities_applied = this.isPresent(
        searchAttributes.priorities
      )
      properties.export_type_applied = this.isPresent(searchAttributes.types)
      properties.filters_applied = filtersApplied
    }

    if (event.detail.actionExportResults) {
      properties.action_export_results = event.detail.actionExportResults
    }

    analytics.track(eventName, properties)
  }

  sendActionsPaginationClicked() {
    analytics.track('Pagination Clicked')
  }

  sendTagsAdded(event) {
    const modelObject = event.detail.model

    if (modelObject == 'issues') {
      analytics.track('Data Added to Issue', defaultPropertiesAction)
    }
    if (modelObject == 'labels') {
      analytics.track('Label Added', defaultPropertiesAction)
    }
  }

  getExportNames(exportIds) {
    const exportNames = []

    exportIds.forEach((id) => {
      exportNames.push(
        document.querySelector(`label[data-analytics-export-id="${id}"]`)
          .innerText
      )
    })

    return exportNames
  }

  getFiltersApplied(arrayOfFilters, filtersObject) {
    const filtersApplied = []

    arrayOfFilters.forEach((filter) => {
      if (this.isFilterApplied(filter, filtersObject)) {
        filtersApplied.push(filter)
      }
    })

    return filtersApplied
  }

  isFilterApplied(filter, filtersObject) {
    const filtersKeys = Object.keys(filtersObject)

    switch (filter) {
      case 'date':
        if (this.isDateAttributePresent(filtersObject)) {
          return true
        }
        break
      case 'source':
      case 'type':
      case 'labels':
      case 'issues':
      case 'location':
      case 'priority':
      case 'position':
      case 'document_type':
        const formAttr = this.filterToFormAttr[filter]
        const regex = new RegExp(formAttr) || null

        for (const filter in filtersKeys) {
          if (regex && regex.test(filtersKeys[filter])) {
            return true
          }
        }
        break
    }
    return false
  }

  isDateAttributePresent(filtersObject) {
    return (
      this.isPresent(filtersObject.date_to) ||
      this.isPresent(filtersObject.date_from) ||
      this.isPresent(filtersObject.start_date) ||
      this.isPresent(filtersObject.end_date)
    )
  }

  isPresent(value) {
    return value != undefined && value != ''
  }

  get pathnameToEventName() {
    return {
      '/actions': 'Actions Index Page',
      '/actions/new': ACTION_CREATE_PAGE_EVENT,
      '/exports/new': 'Export Builder Page',
      '/exports': 'Export Index Page',
      '/exports/edit': 'Export Edit Page'
    }
  }

  get pathnameToEventProperties() {
    return {
      '/actions/new': defaultPropertiesAction,
      '/actions': defaultPropertiesAction
    }
  }

  get filterToFormAttr() {
    return {
      source: 'export_filter_source',
      type: 'export_filter_type',
      labels: 'labels_ids',
      issues: 'issues_ids',
      location: 'locations',
      priority: 'priorities',
      position: 'positions',
      document_type: 'types'
    }
  }
}
